import { useEffect, useRef, useState } from "react";
import { X, Lock, Loader2, AlertTriangle } from "lucide-react";
import toast from "react-hot-toast";
import { format } from "date-fns";
import { cn } from "@/utils/cn";
import { AddMeetingForm } from "./AddMeetingForm";
import { useDraftMeeting, useDraftMeetingHistory, useLockMeeting } from "@/services/draftCalendar";
import type { FiscalYearResponse, DraftMeetingResponse } from "@/types/draftCalendar";
import { Statuses } from "@/utils/statuses";
import { ADD_MEETING_ID, MEETING_FLYOUT_ID, MEETING_FLYOUT_LABEL, DC_COMMON_TEXT } from "@/utils/draftCalendarFields";

type FlyoutTab = "details" | "history";

interface MeetingFlyoutProps {
  meetingId: number;
  fiscalYear: FiscalYearResponse | undefined;
  businesses: Record<string, string[]>;
  meetingTypes: Record<string, string[]>;
  /** Business → mapped meeting type names, used to restrict Meeting Type options per selected Business for Business Approvers */
  mappedBusinessMeetingTypes?: Record<string, string[]>;
  attendeeLevels: string[];
  locations: string[];
  duDeclinedReasons?: string[];
  isViewOnly: boolean;
  lockedBy: string | null;
  canEdit: boolean;
  /** Whether the current user can copy this meeting to a new one (may differ from canEdit) */
  canCopyToNew?: boolean;
  isBusinessApprover?: boolean;
  isBusinessReviewer?: boolean;
  canEditSignOffStatus?: boolean;
  /** Whether the Sign-Off Status dropdown is shown (true for existing meetings) */
  canViewSignOffRequest?: boolean;
  /** Whether the current user is responsible for sign-off on this meeting */
  canSubmitSignOff?: boolean;
  /** Mirrors Blazor CanSubmitToApprover — shows signed-off-by message for admin/scheduler in the else branch */
  canSubmit?: boolean;
  signOffStatus?: string | null;
  /** Pre-populated data for Copy To New (only used when meetingId === 0) */
  copyFromData?: DraftMeetingResponse;
  /** Fields selected for copy — only defined when opening via Copy To New */
  copySelectedFields?: Set<string>;
  /** Whether the current user can push a meeting to Summit */
  canPushToSummit?: boolean;
  /** Current user's display name — used for locking */
  currentUserName?: string;
  /** Current user's email — used for locking */
  currentUserEmail?: string;
  onEdit: () => void;
  onSaved: () => void;
  onClose: () => void;
  /** Called when user clicks Back from edit mode — returns to view mode without closing the panel */
  onBack?: () => void;
  /** Called when user clicks Copy To New — page handles fetching data and opening create form */
  onCopyToNew?: () => void;
  /** Called when user clicks Push To Summit — page handles confirmation + mutation */
  onPushToSummit?: () => void;
}

/**
 * Right-side flyout panel for viewing / editing a draft meeting.
 * Wraps AddMeetingForm with a title bar, lock indicator, and Edit/Cancel controls.
 * Mirrors Blazor CreateDraftEvent popup pattern.
 */
export function MeetingFlyout({
  meetingId,
  fiscalYear,
  businesses,
  meetingTypes,
  mappedBusinessMeetingTypes,
  attendeeLevels,
  locations,
  duDeclinedReasons = [],
  isViewOnly,
  lockedBy,
  canEdit,
  canCopyToNew = canEdit,
  isBusinessApprover = false,
  isBusinessReviewer = false,
  canEditSignOffStatus = false,
  canViewSignOffRequest = false,
  canSubmitSignOff = false,
  canSubmit = false,
  signOffStatus = null,
  copyFromData,
  copySelectedFields,
  canPushToSummit = false,
  currentUserName = "",
  currentUserEmail = "",
  onEdit,
  onSaved,
  onClose,
  onBack,
  onCopyToNew,
  onPushToSummit
}: MeetingFlyoutProps) {
  const lockMeeting = useLockMeeting();
  const isNew = meetingId === 0;
  const [isSaving, setIsSaving] = useState(false);
  const [isLocking, setIsLocking] = useState(false);

  // Load meeting details to show the signed-off-by banner in the flyout chrome
  // (mirrors Blazor ViewSignedOffByMsg shown at DraftCalender window level, not inside the form)
  // React Query serves this from cache — AddMeetingForm uses the same query key, no duplicate request.
  const { data: meetingDetail } = useDraftMeeting(meetingId);
  const isSignedOffOrRejected =
    !isNew &&
    meetingDetail &&
    (meetingDetail.signOffStatus?.toLowerCase() === 'signed off' ||
     meetingDetail.signOffStatus?.toLowerCase() === 'rejected');
  const signedOffByName = meetingDetail?.draftEventSignedOffBy ?? '';
  const signedOffDateRaw = meetingDetail?.draftEventSignedOffDate ?? '';
  const signedOffDate = signedOffDateRaw
    ? (() => { try { return format(new Date(signedOffDateRaw), "MM/dd/yyyy h:mm aa 'CT'"); } catch { return signedOffDateRaw; } })()
    : '';
  const signedOffLabel = meetingDetail?.signOffStatus?.toLowerCase() === 'rejected' ? 'Rejected' : 'Closed';

  const [activeTab, setActiveTab] = useState<FlyoutTab>("details");

  // Snap back to Details whenever editing starts (mirrors CreateSoftHoldDialog)
  useEffect(() => {
    if (!isViewOnly) setActiveTab("details");
  }, [isViewOnly]);

  // Load history for the History tab — only fetches when tab is active (enabled=false until then)
  const { data: historyItems = [], isPending: isMeetingPending } = useDraftMeetingHistory(
    meetingId,
    !isNew && activeTab === "history"
  );

  const title = isNew
    ? MEETING_FLYOUT_LABEL.TITLE_ADD
    : isViewOnly
    ? MEETING_FLYOUT_LABEL.TITLE_DETAILS
    : MEETING_FLYOUT_LABEL.TITLE_EDIT;

  /** Lock the meeting before entering edit mode — mirrors Blazor OnEditDraftMeeting + LockUnLockMeeting(false) */
  async function handleEditClick() {
    if (isNew) { onEdit(); return; }
    setIsLocking(true);
    try {
      const locked = await lockMeeting.mutateAsync({
        draftMeetingId: meetingId,
        lockedBy: currentUserName,
        lockedByUserEmail: currentUserEmail,
        isUnlockReq: false,
        lockedDateTime: new Date().toISOString(),
      });
      if (!locked) {
        // Lock failed — another user holds the lock; refresh to get their name
        //toast.error("This meeting is currently being edited by another user.");
        // Re-fetch to surface meetingLockedBy from the API
        onEdit(); // parent can optionally re-fetch; we stay view-only by not calling it, but Blazor does refresh
        // Match Blazor: IsLocked=false → RefreshMeetingDetails → stays IsViewOnly=true
        // We don't call onEdit() so view-only is preserved
        return;
      }
      onEdit();
    } catch {
      toast.error("Failed to lock the meeting for editing. Please try again.");
    } finally {
      setIsLocking(false);
    }
  }

  /** Unlock the meeting — called on save or close when in edit mode */
  async function handleUnlock() {
    if (isNew || isViewOnly) return;
    try {
      await lockMeeting.mutateAsync({
        draftMeetingId: meetingId,
        lockedBy: currentUserName,
        lockedByUserEmail: currentUserEmail,
        isUnlockReq: true,
        lockedDateTime: new Date().toISOString(),
      });
    } catch (error) {
      console.error("[MeetingFlyout] Failed to unlock meeting:", meetingId, error);
    }
  }

  function handleClose() {
    void handleUnlock().catch((err) => console.error("[MeetingFlyout] Unlock on close failed:", err));
    onClose();
  }

  /** Back from edit mode → unlock and return to view mode (panel stays open) */
  function handleBack() {
    void handleUnlock().catch((err) => console.error("[MeetingFlyout] Unlock on back failed:", err));
    if (onBack) { onBack(); } else { onClose(); }
  }

  async function handleSaved() {
    await handleUnlock();
    onSaved();
  }
  const panelRef = useRef<HTMLDivElement>(null);

  // Derives CanEdit from RoleStatusPermissionsList (role × state × signOffStatus):
  //   Admin/Scheduler — all states except CreatedInSummit
  //   Approver       — NewRequest (any signOff) | BusinessReview + None signOff only
  //   BusinessReviewer — never
  //   CreatedInSummit  — never (all roles)
  const effectiveCanEdit = (() => {
    if (!canEdit) return false;
    const state   = meetingDetail?.state;
    const signOff = (meetingDetail?.signOffStatus ?? '').trim().toLowerCase();
    //newid is not null or not empty return false 
    if (state === Statuses.CreatedInSummit ||  (meetingDetail?.newID ?? '' !== '' )) return false;
    if (isBusinessReviewer) return false;
    if (isBusinessApprover) {
      if (state === Statuses.NewRequest) return true;
      if (state === Statuses.ProgramOwnerReviewed && !signOff) return true;
      return false;
    }
    return true; // Admin / Scheduler and others
  })();

  // Mirrors Blazor RoleStatusPermissionsList.GetPermissionMSG(role, state):
  // Only the Approver role shows a permission error banner, based on the meeting's current state.
  const userPermissionErrorMsg: React.ReactNode = (() => {
    if (isNew || !meetingDetail) return "";
    if (!isBusinessApprover) return "";
    const state = meetingDetail.state;
    if (state === Statuses.Removed) {
      return (
        <>This event has been removed. You cannot make further changes. Please contact the Scheduler at <a href="mailto:duscheduling@deloitte.com" className="underline font-medium text-amber-900 hover:text-amber-700">duscheduling@deloitte.com</a> if you need to revert this action.</>
      );
    }
    if (
      state === Statuses.DUReview ||
      state === Statuses.Resolved ||
      state === Statuses.CreatedInSummit ||
      state === Statuses.DUDeclined
    ) {
      
      return <>This meeting is locked. Any further changes must be requested through DU Scheduling at <a href="mailto:duscheduling@deloitte.com" className="underline font-medium text-amber-900 hover:text-amber-700">duscheduling@deloitte.com</a>.</>;
    }
    return "";
  })();

  // Move focus into the panel when it opens.
  useEffect(() => {
    const firstFocusable = panelRef.current?.querySelector<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    firstFocusable?.focus();
  }, []);

  // Trap focus within the dialog panel.
  function handlePanelKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Escape") {
      onClose();
      return;
    }
    if (e.key !== "Tab") return;
    const focusables = Array.from(
      panelRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ) ?? []
    );
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  return (
    <>
      {/* Backdrop — presentational, not interactive for AT */}
      <div
        className="fixed inset-0 z-50 bg-black/30"
        onClick={handleClose}
        aria-hidden="true"
      />
      {/* Dialog panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={MEETING_FLYOUT_ID.DIALOG_TITLE}
        tabIndex={-1}
        className="fixed inset-y-0 right-0 z-50 ml-auto flex h-full w-full max-w-3xl flex-col border-l border-slate-300 bg-white shadow-xl"
        onKeyDown={handlePanelKeyDown}
      >
        {/* Header — black, mirrors CreateSoftHoldDialog */}
        <div className="flex items-center justify-between bg-black px-4 py-3">
          <div className="flex items-center gap-3">
            <h2 id={MEETING_FLYOUT_ID.DIALOG_TITLE} className="text-base font-semibold text-white">
              {title}
            </h2>
          </div>
          <button
            type="button"
            onClick={() => void handleClose()}
            aria-label="Close dialog"
            className="text-white hover:text-slate-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Lock banner */}
        {lockedBy && isViewOnly && !isNew && (
          <div className="flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900">
            <Lock className="h-3.5 w-3.5 flex-shrink-0" />
            Currently being edited by <strong className="ml-1">{lockedBy}</strong>
          </div>
        )}

        {/* Permission error banner — mirrors Blazor UserPermissionErrorMsg (Approver role only) */}
        {userPermissionErrorMsg && isViewOnly && !isNew && (
          <div className="flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-600" />
            <span>{userPermissionErrorMsg}</span>
          </div>
        )}

        {/* Tabs — shown for existing meetings, equal-width like Blazor .equal-width-tabs */}
        {!isNew && (
          <div
            role="tablist"
            aria-label={MEETING_FLYOUT_LABEL.TABLIST_LABEL}
            className="flex items-end gap-0 bg-white px-4 pt-4"
          >
            <button
              role="tab"
              type="button"
              aria-selected={activeTab === "details"}
              onClick={() => setActiveTab("details")}
              style={{ width: "50%" }}
              className={cn(
                "relative -mb-px px-5 py-2.5 text-sm font-semibold transition-colors focus:outline-none",
                activeTab === "details"
                  ? "border border-b-white border-slate-200 bg-black text-white"
                  : "border border-transparent text-slate-600 hover:text-slate-900"
              )}
            >
              {MEETING_FLYOUT_LABEL.TAB_MEETING_DETAILS}
            </button>
            <button
              role="tab"
              type="button"
              aria-selected={activeTab === "history"}
              onClick={() => { if (isViewOnly) setActiveTab("history"); }}
              disabled={!isViewOnly}
              title={!isViewOnly ? MEETING_FLYOUT_LABEL.HISTORY_UNAVAILABLE_WHILE_EDITING : undefined}
              style={{ width: "50%" }}
              className={cn(
                "relative -mb-px px-5 py-2.5 text-sm font-semibold transition-colors focus:outline-none",
                activeTab === "history"
                  ? "border border-b-white border-slate-200 bg-black text-white"
                  : "border border-transparent text-slate-600 hover:text-slate-900",
                !isViewOnly && "cursor-not-allowed opacity-40"
              )}
            >
              {MEETING_FLYOUT_LABEL.TAB_HISTORY}
            </button>
          </div>
        )}

        {/* History tab panel */}
        {!isNew && activeTab === "history" ? (
          <div
            role="tabpanel"
            aria-label={MEETING_FLYOUT_LABEL.HISTORY_PANEL_LABEL}
            className="flex-1 overflow-y-auto px-4 py-4"
          >
            {isMeetingPending ? (
              <div className="flex items-center justify-center py-10 text-sm text-slate-500">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {DC_COMMON_TEXT.FLYOUT_HISTORY_LOADING}
              </div>
            ) : historyItems.length === 0 ? (
              <div className="py-6 text-sm text-slate-500">{DC_COMMON_TEXT.FLYOUT_HISTORY_EMPTY}</div>
            ) : (
              <ul className="space-y-0 border border-slate-200">
                {historyItems.map((item, index) => (
                  <li
                    key={`${item.softHoldId}-${index}`}
                    className={cn(
                      "px-3 py-3 text-sm",
                      index % 2 === 0 ? "bg-white" : "bg-slate-50"
                    )}
                  >
                    <span className="text-slate-800" style={{ fontSize: 14 }}>
                      {item.displayMessage}
                    </span>
                    <br />
                    <span className="text-slate-500" style={{ fontSize: 10 }}>
                      {item.updatedBy}{item.updatedBy && item.createdDate ? ", " : ""}{item.createdDate ?? ""}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          /* Details tab / form */
          <div className="flex flex-1 overflow-hidden">
          <AddMeetingForm
            meetingId={meetingId}
            fiscalYear={fiscalYear}
            businesses={businesses}
            meetingTypes={meetingTypes}
            mappedBusinessMeetingTypes={mappedBusinessMeetingTypes}
            attendeeLevels={attendeeLevels}
            locations={locations}
            duDeclinedReasons={duDeclinedReasons}
            isViewOnly={isViewOnly}
            isBusinessApprover={isBusinessApprover}
            isBusinessReviewer={isBusinessReviewer}
            canEditSignOffStatus={canEditSignOffStatus}
            canViewSignOffRequest={canViewSignOffRequest}
            copyFromData={copyFromData}
            copySelectedFields={copySelectedFields}
            onSaved={() => void handleSaved()}
            onCancel={() => void handleClose()}
            onSavingChange={setIsSaving}
          />
          </div>
        )}

        {/* Footer — mirrors Blazor WindowFooter exclusive if/else branch structure */}
        <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3 gap-2">

          {/* Branch 2: ViewSignedOffByMsg && (IsBusinessApprover || IsBusinessReviewer)
              Shows signed-off-by message ONLY — no action buttons — just the message + close */}
          {isViewOnly && !isNew && isSignedOffOrRejected && (isBusinessApprover || isBusinessReviewer) && (
            <p className="flex-1 mr-2 text-sm text-black" style={{ paddingLeft: 4 }}>
              {meetingDetail?.signOffStatus} by {signedOffByName}
              {signedOffDate ? `, ${signedOffDate}` : ""}.{' '}
              {DC_COMMON_TEXT.FLYOUT_SIGNED_OFF_HELP_TEXT}
            </p>
          )}

          <div className="flex items-center gap-2 shrink-0 ml-auto">
            {/* Branch 3 (else): shown for everyone except approver/reviewer who see the signed-off message */}
            {!(isSignedOffOrRejected && (isBusinessApprover || isBusinessReviewer)) && (
              <>
                {/* ViewSignedOffByMsg && CanSubmitToApprover — signed-off-by line for admin/scheduler */}
                {isViewOnly && !isNew && isSignedOffOrRejected && canSubmit && (
                  <span className="self-center text-sm text-black mr-1">
                    {meetingDetail?.signOffStatus} by {signedOffByName}
                    {signedOffDate ? `, ${signedOffDate}` : ""}.
                  </span>
                )}

                {/* Copy To New: Visible="@CanCopyMeeting" (no role restriction in Blazor) */}
                {isViewOnly && !isNew && canCopyToNew && onCopyToNew && (
                  <button
                    type="button"
                    onClick={onCopyToNew}
                    className="rounded-none border border-slate-900 bg-slate-900 px-4 py-2 text-sm text-white hover:bg-slate-800"
                  >
                    Copy To New
                  </button>
                )}

                {/* Edit: Visible="@(IsViewOnly && !IsBusinessReviewer && !IsSelectedMeetingReadOnly)"
                         Enabled="@(CanEditMeeting && string.IsNullOrEmpty(MeetingLockedBy))"
                    Disabled (not hidden) when lockedBy is set, to give visual feedback */}
                {isViewOnly && !isNew && !isBusinessReviewer && effectiveCanEdit && (
                  <button
                    type="button"
                    onClick={() => void handleEditClick()}
                    disabled={!!lockedBy || isLocking}
                    title={lockedBy ? `${lockedBy} is currently editing this meeting` : undefined}
                    className={cn(
                      "rounded-none border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50",
                      "disabled:opacity-40 disabled:cursor-not-allowed"
                    )}
                  >
                    {isLocking ? "Opening\u2026" : "Edit"}
                  </button>
                )}
              </>
            )}

            {/* Cancel/Back button visibility mirrors Blazor:
                - View mode (isViewOnly): always shown — everyone needs a way to close the panel
                - Edit mode for existing (!isViewOnly && !isNew): Visible="@(!IsViewOnly && !IsBusinessReviewer)"
                - New meeting (!isNew): Visible="@(!IsBusinessReviewer)" */}
            {(isViewOnly || !isBusinessReviewer) && (
              <button
                type="button"
                onClick={!isViewOnly && !isNew ? handleBack : () => void handleClose()}
                className="rounded-none border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                {isViewOnly ? "Cancel" : isNew ? "Cancel" : "Back"}
              </button>
            )}

            {/* Save/Update: Visible="@(!IsViewOnly && !IsBusinessReviewer)" */}
            {!isViewOnly && !isBusinessReviewer && (
              <button
                type="submit"
                form={ADD_MEETING_ID.FORM}
                disabled={isSaving}
                className={cn(
                  "inline-flex items-center gap-2 rounded-none border border-slate-900 bg-slate-900 px-4 py-2 text-sm text-white",
                  "hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed"
                )}
              >
                {isSaving ? "Saving\u2026" : isNew ? "Save" : "Update"}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}